<template lang="pug">
    #member-settings-metric-entries-form
      BNotification(:active.sync="error" type="is-danger" :duration="3000")
        | {{ errorMessage }}
      .super-admin-only.p-5
        .mb-2.p-2.bordered(v-if="$auth.user().isWtAdmin")
          .sub-title.has-text-red ONLY SuperAdmin can access Wellness Metrics.
          .columns.mr-0
            .column
              WtTitle.mb-1(h3) Add or Edit Wellness Metrics
              .sub-title(v-if="!$auth.user().isWtAdmin") Add or Edit Wellness Metrics for this member up to a month in the past.
            .column
              WtTitle
                .p-1(v-if="true")
                  .label.has-text-grey.is-size-7.pl-2 Category
                  BSelect(
                    v-model="metricIndex"
                    :loading="dataLoading"
                    scrollable
                    position="is-bottom-right"
                  )
                    option(
                      v-for="(option, index) in metrics"
                      :value="index"
                      :key="option"
                    ) {{ _.upperFirst(option) }}
          BField(
            label="Start Date"
          )
            BDatepicker(
              placeholder="Click to select..."
              :date-formatter="(date) => this.date(date, 'DATE_FORMAT_DB').formatted"
              v-model="startDate"
              :min-date="minDate"
              :max-date="moment()._d"
            )
          BField(
            label="End Date"
          )
            BDatepicker(
              placeholder="Click to select..."
              :date-formatter="(date) => this.date(date, 'DATE_FORMAT_DB').formatted"
              v-model="endDate"
              :min-date="minDate"
              :max-date="moment()._d"
            )
          BField(
            :label= "metricTypeLabel"
          )
            WtButton(@click.native="fetchMetricData(startDate, endDate)") {{ metricTypeLabel }}
        template(v-if="metricDataFetched")
          .box.bordered
            BField(
              v-for="data in MetricDataWithEmptyDays" :key="data.date"
              :label="date(data.date, 'DATE_FORMAT_FULL_MONTH').formatted"
              horizontal
            )
              span(v-if="!isMoodMetric && !isSleepMetric")
                .label.has-text-grey.is-size-7.pl-2 {{ metricInputLabel }} :
                BInput(
                  :placeholder="metricTypeLabel"
                  v-model="data.metricValue"
                  @blur="saveEntries(data)"
                  @keyup.native.enter="saveEntries(data)"
                  :loading="saveInProgress === data.date"
                )
              WtTile(parent :col="12" v-if="isMoodMetric")
                WtTile(child :col="4" child-class="").is-justified
                  .label.has-text-grey.is-size-7.pl-2 {{ metricInputLabel }} :
                    BInput.ml-2(
                      :placeholder="metricTypeLabel"
                      v-model="data.metricValue"
                    )
                WtTile(child :col="4" child-class="").is-justified
                  BDropdown.ml-4.mt-4( aria-role='list' v-model="data.moodText" scrollable position="is-bottom-right")
                    template(#trigger='{ active }')
                      BButton(:label="data.moodText || 'Choose'" :icon-right="active ? 'menu-up' : 'menu-down'")
                    template(v-for="moodEntry in moodTextEntries")
                      .dropdown-header.p-2.has-text-underlined.has-text-grey {{ moodEntry.header }}
                      BDropdownItem(
                        :key="entryName+index"
                        aria-role='listitem'
                        v-for="(entryName, index) in moodEntry.entries"
                        :value="entryName"
                      ) {{ entryName }}
                WtTile(child :col="4" child-class="").is-justify-content-space-between
                  WtButton.ml-4.mt-4(
                    @click.native="saveEntries(data)"
                    :loading="saveInProgress === data.date"
                  ) save
              WtTile(v-if="isSleepMetric")
                .make-an-entry-sleep-body.pt-4
                  span
                    .label.has-text-grey.is-size-7.pl-2 In to Bed
                    .is-flex.is-align-items-center
                      WtSvgIcon.mr-4(icon="icon_into_bed" width="32px" height="32px")
                      BField
                        BTimepicker(
                          size="is-small"
                          hour-format="12"
                          inline
                          v-model="data.sleepStart"
                          :default-minutes="0"
                        )
                  span
                    .label.has-text-grey.is-size-7.pl-2 Out of Bed
                    .is-flex.is-align-items-center
                      WtSvgIcon.mr-4(icon="icon_out_of_bed" width="32px" height="32px")
                      BField
                        BTimepicker(
                          size="is-small"
                          hour-format="12"
                          inline
                          v-model="data.sleepEnd"
                          :default-minutes="30"
                        )
                  WtButton.mt-5(
                    @click.native="saveEntries(data)"
                    :loading="saveInProgress === data.date"
                  ) save
  </template>

<script>
import { mapActions } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import { numberUtilityMixin } from "@/mixins/numberUtilityMixin";
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";
import WtButton from "@@/WtButton/WtButton";

const NUTRITION_METRIC = "nutrition";
const MOOD_METRIC = "mood";
const SLEEP_METRIC = "sleep";
const WATER_METRIC = "water";
const HEART_METRIC = "heart";
const MINDFULNESS_METRIC = "mindfulness";
const WEIGHT_METRIC = "weight";
export default {
  name: "MemberSettingsEditMetricEntriesForm",
  components: { WtTile, WtTitle, WtSvgIcon, WtButton },
  mixins: [dateUtilityMixin, numberUtilityMixin],
  props: {
    member: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      error: null,
      errorMessage: null,
      startDate: null,
      endDate: null,
      startDateFetched: null,
      endDateFetched: null,
      fetchInProgress: false,
      metricDataFetched: false,
      saveInProgress: false,
      metricData: [],
      metricIndex: 0,
      metrics: [
        "nutrition",
        "water",
        "mood",
        "sleep",
        "heart",
        "mindfulness",
        "weight"
      ],
      metricInputLabelMap: [
        "Nutrition value (1-10)",
        "No of glasses of water (0-16)",
        "Mood value (1-5)",
        "Sleep hours",
        "Heart rate (45-130 bpm)",
        "No of mindfulness sessions (0-20)",
        "Enter weight (30-700 kg)"
      ],
      moodText: "",
      moodTextEntries: [],
      dataLoading: false
    };
  },
  computed: {
    MetricDataWithEmptyDays() {
      let MetricDataWithEmptyDays = [];
      for (
        let m = this.moment(this.endDateFetched);
        m.isSameOrAfter(this.startDateFetched);
        m.subtract(1, "days")
      ) {
        let metricDataExistsForDay = this.metricData.find(data => {
          return this.moment(data.date).isSame(m, "day");
        });
        if (metricDataExistsForDay) {
          let newData = {
            id: metricDataExistsForDay.date,
            date: metricDataExistsForDay.date,
            metricValue: metricDataExistsForDay.value
          };
          if (this.metricType === "sleep") {
            newData.sleepStart = this.parseStartTime(
              metricDataExistsForDay.text
            );
            newData.sleepEnd = this.parseEndTime(metricDataExistsForDay.text);
          }
          if (this.metricType === "mood") {
            newData.moodText = metricDataExistsForDay.text;
          }
          MetricDataWithEmptyDays.push(newData);
        } else {
          let newData = {
            id: null,
            date: m.format("YYYY-MM-DD"),
            metricValue: 0
          };
          if (this.metricType === "sleep") {
            newData.sleepStart = new Date(new Date().setHours(22, 30, 0));
            newData.sleepEnd = new Date(new Date().setHours(6, 30, 0));
          }
          if (this.metricType === "mood") {
            newData.moodText = "";
          }
          MetricDataWithEmptyDays.push(newData);
        }
      }
      return MetricDataWithEmptyDays;
    },
    minDate() {
      return this.moment(this.member.registeredDate).subtract(1, "days")._d;
    },
    metricTypeLabel() {
      return "Fetch " + this.metricType;
    },
    metricType() {
      return this.metrics[this.metricIndex];
    },
    metricInputLabel() {
      return this.metricInputLabelMap[this.metricIndex];
    },
    isSleepMetric() {
      return this.metricType === SLEEP_METRIC;
    },
    isMoodMetric() {
      return this.metricType === MOOD_METRIC;
    }
  },
  watch: {
    metricIndex() {
      let endDate = this.endDate ?? this.moment();
      let startDate = this.startDate ?? this.moment().subtract(30, "days");
      if (this.moment(this.member.registeredDate).isSameOrAfter(startDate)) {
        startDate = this.moment(this.member.registeredDate);
      }
      this.fetchMetricData(startDate, endDate);
    }
  },
  async mounted() {
    let endDate = this.moment();
    let startDate = this.moment().subtract(30, "days");
    if (this.moment(this.member.registeredDate).isSameOrAfter(startDate)) {
      startDate = this.moment(this.member.registeredDate);
    }
    await this.fetchMetricData(startDate, endDate);
    await this.fetchMoodTextEntries();
  },
  methods: {
    ...mapActions("members", [
      "fetchMemberWellnessDataByFilter",
      "updateMemberWellnessData"
    ]),
    ...mapActions(["snackBar"]),
    async fetchMetricData(startDate, endDate) {
      this.metricDataFetched = false;
      this.fetchInProgress = true;
      this.startDateFetched = startDate;
      this.endDateFetched = endDate;
      const metricDataResponse = await this.fetchMemberWellnessDataByFilter({
        startDate: this.formatDateForRequest(startDate),
        endDate: this.formatDateForRequest(endDate),
        memberId: this.member.id,
        perPage: this.daysBetweenInclusive(startDate, endDate),
        metric: this.metricType
      });
      this.metricData = metricDataResponse.data.data;
      this.metricDataFetched = true;
      this.fetchInProgress = false;
      this.startDateFetched = startDate;
      this.endDateFetched = endDate;
    },
    validateSteps(data) {
      if (data.steps <= 0 || !Number.isInteger(parseInt(data.steps))) {
        throw new Error("Steps must be a positive integer greater than 0.");
      }
    },
    parseStartTime(data) {
      return this.moment(this._.get(data.split(","), "[0]", ""))._d;
    },
    parseEndTime(data) {
      return this.moment(this._.get(data.split(","), "[1]", ""))._d;
    },
    totalSleepTimeInMinutes(startTime, endTime) {
      const startObj = this.moment(startTime);
      const endObj = this.moment(endTime);
      let hourDiff = endObj.hours() - startObj.hours();
      let minDiff = endObj.minutes() - startObj.minutes();
      if (hourDiff < 0) hourDiff = 24 + hourDiff;
      else if (hourDiff === 0 && minDiff < 0) hourDiff = 23;
      if (minDiff < 0) {
        minDiff = 60 + minDiff;
        hourDiff = hourDiff - 1;
      }
      return hourDiff * 60 + minDiff;
    },
    validateMetrics(data) {
      if (
        (data.metricValue < 1 ||
          data.metricValue > 10 ||
          !Number.isInteger(parseInt(data.metricValue))) &&
        this.metricType === NUTRITION_METRIC
      ) {
        throw new Error(
          "For nutrition, the metric value must be between 1 and 10."
        );
      } else if (
        (data.metricValue < 1 ||
          data.metricValue > 5 ||
          !Number.isInteger(parseInt(data.metricValue))) &&
        this.metricType === MOOD_METRIC
      ) {
        throw new Error("For mood, the metric value must be between 1 and 5.");
      } else if (
        (data.metricValue < 0 ||
          data.metricValue > 16 ||
          !Number.isInteger(parseInt(data.metricValue))) &&
        this.metricType === WATER_METRIC
      ) {
        throw new Error(
          "For water, the metric value must be between 0 and 16."
        );
      } else if (
        (data.metricValue < 45 ||
          data.metricValue > 130 ||
          !Number.isInteger(parseInt(data.metricValue))) &&
        this.metricType === HEART_METRIC
      ) {
        throw new Error(
          "For heart, the metric value must be between 45 and 130."
        );
      } else if (
        (data.metricValue < 0 ||
          data.metricValue > 20 ||
          !Number.isInteger(parseInt(data.metricValue))) &&
        this.metricType === MINDFULNESS_METRIC
      ) {
        throw new Error(
          "For mindfulness, the metric value must be between 0 and 20."
        );
      } else if (
        (data.metricValue < 30 ||
          data.metricValue > 700 ||
          !Number.isInteger(parseInt(data.metricValue))) &&
        this.metricType === WEIGHT_METRIC
      ) {
        throw new Error(
          "For weight, the metric value must be between 30 and 700."
        );
      }
    },
    async saveEntries(data) {
      this.saveInProgress = data.date;
      if (this.metricType === SLEEP_METRIC) {
        data.metricValue = this.totalSleepTimeInMinutes(
          data.sleepStart,
          data.sleepEnd
        );
      }
      try {
        this.validateMetrics(data);
        await this.updateMemberWellnessData({
          wellnessMetricsData: [data],
          metricType: this.metricType,
          memberId: this.member.id
        });
      } catch (error) {
        if (error?.error) {
          this.$scrollTo("#member-settings-metric-entries-form", 500, {
            offset: -70
          });
          this.error = true;
          this.errorMessage = error.error.message;
        } else {
          this.$scrollTo("#member-settings-metric-entries-form", 500, {
            offset: -70
          });
          this.error = true;
          this.errorMessage = error.message;
        }
      }
      this.saveInProgress = false;
    },
    async fetchMoodTextEntries() {
      try {
        const response = await this.axios.get("journals/mood-entries");
        this.moodTextEntries = response.data;
      } catch (error) {
        this.error = true;
      }
    }
  }
};
</script>
<style lang="sass" scoped>
.super-admin-only
  border: 1px solid red
.make-an-entry-sleep-body
  display: flex
  justify-content: space-between
  flex-wrap: wrap
  width: 100%
  max-width: 768px
</style>
